import { useGetExerciseTypes } from '#api/exercise-types';
import { useDashboardChartFormProcess } from '#pages/DashboardChartForm/hooks/useDashboardChartFormProcess';
import { useDashboardCharFormExerciseState } from '#pages/DashboardChartForm/states/dashboardCharFormExerciseState';
import {
  useExercisePresetChartRecoil,
  useExercisePresetRecoil
} from '#states/exercisePresetsState';
import { IExercisePresetWorkout } from '#types/exercisePresets';
import React, { PropsWithChildren } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { ExercisePlus } from './ExercisePlus';
import { ExerciseSearch } from './ExerciseSearch';
import { SetList } from './SetList';

interface ExerciseListProps extends PropsWithChildren {
  exerciseList: IExercisePresetWorkout[];
}

/**
 * @param exerciseList - 운동 선택 또는 프리셋 추가 페이지에서 선택한 운동 리스트
 * @returns
 */
export const ExerciseList = ({ exerciseList, children }: ExerciseListProps) => {
  // api로 검색한 모든 운동 리스트
  const { data: exerciseTypes } = useGetExerciseTypes();

  const { pathname } = useLocation();

  const { setRecords } = useDashboardCharFormExerciseState();

  const { setExercisePresetChart } = useExercisePresetChartRecoil();

  const { tab } = useDashboardChartFormProcess();

  // 사용자가 입력한 프리셋 정보
  const { setExercisePresets } = useExercisePresetRecoil();

  const isDashboardChartFormPage = tab === '폼' && !pathname.includes('/edit');

  const { setTab } = useDashboardChartFormProcess();

  if (!exerciseList || !exerciseTypes) return <div></div>;

  return (
    <>
      <div className="mt-20">
        <div className="mb-12 flex justify-between">
          <div>
            운동 내역
            <Requierd />
          </div>
          {isDashboardChartFormPage && (
            <PresetButton
              onClick={() => {
                setRecords([]);
                setExercisePresetChart([]);
                setExercisePresets({ name: '', exercisePresetWorkout: [] });
                setTab('프리셋 선택');
              }}
            >
              프리셋
            </PresetButton>
          )}
        </div>

        <>
          {exerciseList.map((exercisPresetWorkout, index) => {
            const defaultExerciseKeyword = exerciseTypes.data.find(
              item => item.id === exercisPresetWorkout.exerciseTypeId
            )?.name;

            // 하위 자식 컴포넌트에 전달할 props
            const additionalProps = {
              index
            };

            // Clone children and add extraProps to them
            const childrenWithProps = React.Children.map(children, child => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, { ...additionalProps });
              }
              return child;
            });

            return (
              <div key={index} className="mb-12">
                <div
                  style={{
                    display: 'flex',
                    width: '20px',
                    height: '20px',
                    padding: '4px 8px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    borderRadius: '4px',
                    background: 'var(--bg-500, #373245)',
                    marginBottom: '8px'
                  }}
                >
                  <div
                    style={{
                      color: 'var(--Gray-Scale-500, #948F9E)',
                      textAlign: 'center',
                      fontFamily: '"Pretendard Variable"',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '16px'
                    }}
                  >
                    {index + 1}
                  </div>
                </div>

                <ExerciseSearch
                  index={index}
                  defaultExerciseKeyword={defaultExerciseKeyword}
                />

                <SetList sets={exercisPresetWorkout.sets} index={index} />

                {childrenWithProps}
              </div>
            );
          })}
        </>
      </div>
      <ExercisePlus />
    </>
  );
};

const Requierd = () => {
  return <StyledRequired>*</StyledRequired>;
};

const StyledRequired = styled.div`
  color: var(--Semantic-red, #e13a3a);

  margin-left: 4px;

  /* NEW/headline_01_medium */
  font-family: 'Pretendard Variable';
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px; /* 122.222% */
  letter-spacing: -0.408px;
  display: inline-block;
`;

const PresetButton = styled.button`
  display: flex;
  padding: 8px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: 8px;
  background: var(--bg-600, #2e2a3a);

  color: var(--Gray-Scale-400, #bcb8c3);

  /* NEW/body_03_medium */
  font-family: 'Pretendard Variable';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.5px;
`;
